<template>
  <footer class="wrktp-footer">
    <div class="wrktp-footer__top">
      <b-container>
        <b-row>
          <b-col cols="12" sm="6" lg="4" class="mb-4">
            <h2 class="wrktp-footer__heading">Jobnet.AG</h2>
            <p class="wrktp-footer__desc">{{ content.description }}</p>
            <ul class="wrktp-footer__contacts">
              <li>
                <b-icon icon="geo-alt" aria-hidden="true" />
                <span>{{ content.registeredOffice }}:<br>Kolonnenstraße 8<br>10827 Berlin</span>
              </li>
              <li>
                <b-icon icon="envelope" aria-hidden="true" />
                <a href="mailto:info@jobnet.works">info@jobnet.works</a>
              </li>
              <li>
                <b-icon icon="globe2" aria-hidden="true" />
                <a href="https://www.jobnet.works" target="_blank" rel="noopener noreferrer">www.jobnet.works</a>
              </li>
              <li>
                <b-icon icon="telephone" aria-hidden="true" />
                <a href="tel:+493057700120">+49.30.5770012-0</a>
              </li>
              <li>
                <b-icon icon="whatsapp" aria-hidden="true" />
                <a href="https://wa.me/+4930577001280" target="_blank" rel="noopener noreferrer">
                  WhatsApp: +49 305 7700 1280
                </a>
              </li>
              <li>
                <b-icon icon="headset" aria-hidden="true" />
                <span>
                  <a href="mailto:hotline@jobnet.solutions" class="d-block">hotline@jobnet.solutions</a>
                  <a href="tel:+4930577001255" class="d-block wrktp-footer__small">
                    {{ content.hotlineGermany }}: +49.30.5770012-55
                  </a>
                  <a href="tel:+4317481010" class="d-block wrktp-footer__small">
                    {{ content.hotlineAustria }}: +43.1.7481010
                  </a>
                </span>
              </li>
            </ul>
          </b-col>

          <b-col cols="6" sm="3" lg="2" class="mb-4">
            <nav aria-labelledby="wrktp-footer-pages">
              <h2 id="wrktp-footer-pages" class="wrktp-footer__heading">{{ content.pagesHeading }}</h2>
              <ul class="list-unstyled m-0">
                <li v-for="link in pages" :key="link.path"><a :href="url(link)" class="wrktp-footer__link">{{ link.text }}</a></li>
                <li><a :href="url(nav.aboutUs)" class="wrktp-footer__link">{{ nav.aboutUs.text }}</a></li>
                <li><a :href="url(nav.contact)" class="wrktp-footer__link">{{ nav.contact.text }}</a></li>
              </ul>
            </nav>
          </b-col>

          <b-col cols="6" sm="3" lg="3" class="mb-4">
            <nav aria-labelledby="wrktp-footer-services">
              <h2 id="wrktp-footer-services" class="wrktp-footer__heading">{{ content.servicesHeading }}</h2>
              <a :href="url(nav.recruitment)" class="wrktp-footer__link">{{ nav.recruitment.text }}</a>
              <a :href="url(nav.jobPlacement)" class="wrktp-footer__link">{{ nav.jobPlacement.text }}</a>
              <a :href="url(nav.avgsMpav)" class="wrktp-footer__link wrktp-footer__link--sub">
                <b-icon icon="arrow-return-right" aria-hidden="true" class="mr-1" />{{ nav.avgsMpav.text }}
              </a>
              <a :href="url(nav.itOutstaffing)" class="wrktp-footer__link">{{ nav.itOutstaffing.text }}</a>
            </nav>
          </b-col>

          <b-col cols="12" sm="6" lg="3" class="mb-4">
            <h2 class="wrktp-footer__heading">
              <b-icon icon="building" aria-hidden="true" class="wrktp-footer__heading-icon mr-2" />
              {{ content.companyHeading }}
            </h2>
            <dl class="wrktp-footer__company">
              <div>
                <dt>{{ company.company }}</dt>
                <dd>Jobnet.AG</dd>
              </div>
              <div>
                <dt>{{ company.office }}</dt>
                <dd>Kolonnenstraße 8, 10827 Berlin</dd>
              </div>
              <div>
                <dt>{{ company.azav }}</dt>
                <dd>{{ company.azavValue }}</dd>
              </div>
              <div>
                <dt>{{ company.certBody }}</dt>
                <dd>Certuria Certification Germany GmbH</dd>
              </div>
              <div>
                <dt>{{ company.hotlineDe }}</dt>
                <dd><a href="tel:+4930577001255">+49 30 5770012-55</a></dd>
              </div>
              <div>
                <dt>{{ company.hotlineAt }}</dt>
                <dd><a href="tel:+4317481010">+43 1 7481010</a></dd>
              </div>
            </dl>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <div class="wrktp-footer__bottom">
      <b-container class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center">
        <p class="m-0">© {{ year }} Jobnet.AG. {{ content.copyright }}</p>
        <nav :aria-label="content.legalLabel">
          <ul class="list-unstyled d-flex m-0">
            <li><a :href="url(content.privacy)" target="_blank" rel="noopener" class="px-2">{{ content.privacy.text }}</a></li>
            <li><a :href="imprintUrl" target="_blank" rel="noopener" class="px-2">{{ content.imprint }}</a></li>
          </ul>
        </nav>
      </b-container>
    </div>
  </footer>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import {
  WrktpLink, WRKTP_IMPRINT_URL, wrktpContent, wrktpLang, wrktpUrl,
} from '@/config/wrktp';

@Component
export default class FooterComponentWrktp extends Vue {
  imprintUrl = WRKTP_IMPRINT_URL;

  year = new Date().getFullYear();

  get lang() {
    return wrktpLang(AppModule.language);
  }

  get content() {
    return wrktpContent[this.lang];
  }

  get nav() {
    return this.content.nav;
  }

  get company() {
    return this.content.company;
  }

  // Footer "Pages" column; AVGS MPAV is listed only under Services
  get pages() {
    const { nav } = this;
    return [nav.welcome, nav.recruitment, nav.jobPlacement, nav.itOutstaffing];
  }

  url(link: WrktpLink) {
    return wrktpUrl(this.lang, link.path);
  }
}
</script>

<style lang="scss" scoped>
$wrktp-orange: #f1b034;

// Same prefix as the global portal styles, otherwise their link/heading/nav-link rules win
[theme="default"] body.portal {
  .wrktp-footer {
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background-color: #111111;
    color: #ffffff;

    a {
      color: #aaaaaa;
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover,
      &:focus-visible {
        color: $wrktp-orange;
        text-decoration: none;
      }
    }

    &__top {
      border-top: 1px solid #2e2e2e;
      padding-top: 52px;
    }

    &__heading {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
      color: #ffffff;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }

    &__heading-icon {
      color: $wrktp-orange;
      font-size: 0.85rem;
    }

    &__desc {
      margin-bottom: 1.125rem;
      color: #888888;
      font-size: 0.8rem;
      line-height: 1.75;
    }

    &__contacts {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        display: flex;
        align-items: flex-start;
        gap: 0.55rem;
        color: #888888;
        font-size: 0.8rem;
        line-height: 1.45;
      }

      .b-icon {
        flex-shrink: 0;
        margin-top: 2px;
        color: $wrktp-orange;
        font-size: 0.85rem;
      }
    }

    &__small {
      font-size: 0.8rem;
      opacity: 0.75;
    }

    &__link {
      display: block;
      margin-bottom: 0.5rem;
      font-size: 0.8rem;
      line-height: 1.4;

      &--sub {
        padding-left: 1.25rem;
        font-size: 0.85rem;
        opacity: 0.8;

        .b-icon {
          font-size: 0.7rem;
          opacity: 0.6;
        }
      }
    }

    &__company {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      margin: 0;
      font-size: 0.8rem;
      line-height: 1.45;

      dt {
        color: #555555;
        font-size: 0.68rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      dd {
        margin: 0;
        color: #aaaaaa;
      }
    }

    &__bottom {
      margin-top: 20px;
      padding: 1rem 0;
      border-top: 1px solid #252525;
      color: #555555;
      font-size: 0.75rem;

      .container {
        gap: 0.5rem;
      }

      a {
        color: #555555;
      }
    }
  }
}
</style>
